import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import "bootstrap/dist/js/bootstrap.bundle.min.js"; // Import Bootstrap JavaScript
import "../../styles/dashboard.css";
import ChartComponent from './charts';
import useHandleErrors from '../../utils/user.utils';
import { toast, ToastContainer } from 'react-toastify';
import { useUser } from '../../utils/userContext';


const Dashboard = () => {
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [statusCounts, setStatusCounts] = useState({
    Active: 0,
    'In Flight': 0,
    'On Hold': 0,
    Inactive: 0,
    Assigned: 0,
    Unassigned: 0
  });
  const [submittedCount, setSubmittedCount] = useState(0);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const handleErrors = useHandleErrors();
  const { user } = useUser();
  const hasFetchedRef = useRef(false);


  const fetchProjectDetails = async () => {
    try {
      const path = `/getAssignProjectDetails?employeeId=${user?.EmployeeId}`;
      const URL = process.env.REACT_APP_BE_URL + path;
      const response = await axios.get(URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.status === 200) {
        setAssignedProjects(response.data.listProjects);
      } else {
        handleErrors(response)
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          // Handle 404 status code separately
          setAssignedProjects([]);
        } else {
          handleErrors(error.response);
        }
      } else {
        toast.error("Something went wrong. Please try again later.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchProjectDetails();
        await fetchTimesheetDetails();
        await fetchPolicyDetails();
        await fetchRequestDetails();
        await fetchPayslipDetails();
        await getPersonalDetails();
        if (user?.EmployeeId && token) {
          await fetchLeaveBalance();
        }
        await fetchHolidays();
        await fetchAnnouncements();
      } catch (error) {
        if (error.response) {
          if (error.response && error.response.status === 401) {
            toast.error("Session expired. Please log in again.");
            navigate("/")
          } else {
            handleErrors(error.response);
          }
        } else {
          toast.error("Something went wrong. Please try again later.", {
            position: toast.POSITION.TOP_CENTER,
          });
        }
      }
    };
    if (!hasFetchedRef.current) {
      fetchData();
      hasFetchedRef.current = true;
    }
  }, [user?.EmployeeId, token]);


  useEffect(() => {
    const counts = assignedProjects.reduce((acc, project) => {
      acc[project.projectStatus] = (acc[project.projectStatus] || 0) + 1;
      return acc;
    }, {});

    setStatusCounts({
      Active: counts['Active'] || 0,
      'In Flight': counts['In Flight'] || 0,
      'On Hold': counts['On Hold'] || 0,
      Inactive: counts['Inactive'] || 0,
      Assigned: counts['Assigned'] || 0,
      Unassigned: counts['Unassigned'] || 0
    });
  }, [assignedProjects]);

  // 2nd one
  const fetchTimesheetDetails = async () => {
    try {
      const path = `/viewTimesheet?employeeId=${user?.EmployeeId}`;
      const URL = process.env.REACT_APP_BE_URL + path;
      const response = await axios.get(URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const timesheetDetails = response.data.listTimesheetDetails || [];
        let submittedCount = 0;

        timesheetDetails.forEach(timesheet => {
          const summaries = timesheet.timesheetSummary || [];
          submittedCount += summaries.filter(summary => summary.status === 'Submitted')?.length;
        });

        setSubmittedCount(submittedCount);
      } else {
        handleErrors(response);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          // Handle 404 status code separately
          setSubmittedCount(submittedCount);
        } else {
          handleErrors(error.response);
        }
      } else {
        toast.error("Something went wrong. Please try again later.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  };

  const handleCardClick = () => {
    navigate('/ViewTimesheet');
  };

  // 3rd one
  const [leaveData, setLeaveData] = useState(null);

  const fetchLeaveBalance = async () => {
    if (user && user.EmployeeId) {
      const path = `/getLeaveBalance?employeeId=${user.EmployeeId}`;
      const URL = process.env.REACT_APP_BE_URL + path;

      try {
        const response = await axios.get(URL, {
          headers: {
            Authorization: `Bearer ${token}`
          },
        });
        setLeaveData(response.data.userLeaveBalance[0]);
      } catch (error) {
        console.error('Error fetching leave data:', error);
      }
    }
  };

  const toCamelCase = (str) => {
    return str.replace(/\w\S*/g, (word) =>
      word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
    );
  };

  const totalLeaves = leaveData?.leaveBalance.reduce((total, leave) => total + leave.noOfLeaves, 0);
  const approvedRequests = leaveData?.leaveRequests.find(request => request.status.toLowerCase() === 'approved')?.noOfRequests || 0;
  const submittedRequests = leaveData?.leaveRequests.find(request => request.status.toLowerCase() === 'submitted')?.noOfRequests || 0;
  const RejectedRequests = leaveData?.leaveRequests.find(request => request.status.toLowerCase() === 'rejected')?.noOfRequests || 0;

  // 4th one 
  const [policies, setPolicies] = useState([]);

  const fetchPolicyDetails = async () => {
    try {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const path = `/documentReports?employeeId=${user.EmployeeId}&documentType=Insurance&year=${currentYear}`;
      const URL = process.env.REACT_APP_BE_URL + path;
      const response = await axios.get(URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.status === 200) {
        setPolicies(response.data);
      }
      else {
        handleErrors(response)
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          // Handle 404 status code separately
          setPolicies([]);
        } else {
          handleErrors(error.response);
        }
      } else {
        toast.error("Something went wrong. Please try again later.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  }

  // const getFileName = (url) => {
  //   return url.substring(url.lastIndexOf('/') + 1);
  // };

  // sthone
  const [requests, setRequests] = useState([]);

  const fetchRequestDetails = async () => {
    try {
      const path = `/getSupportAndRequests?employeeId=${user?.EmployeeId}`;
      const URL = process.env.REACT_APP_BE_URL + path;
      const response = await axios.get(URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.status === 200) {
        setRequests(response.data.supportRequests);
      } else {
        handleErrors(response)
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          // Handle 404 status code separately
          setRequests([]);
        } else {
          handleErrors(error.response);
        }
      } else {
        toast.error("Something went wrong. Please try again later.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  }

  // 6th one
  const [selectedPayslipMonth, setSelectedPayslipMonth] = useState('');
  const [selectedPayslipYear, setSelectedPayslipYear] = useState('');
  const [selectedPayslipPath, setSelectedPayslipPath] = useState('');
 
  const fetchPayslipDetails = async () => {
    try {
    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleString("default", { month: "long" }).toLowerCase().trim();
    const currentYear = currentDate.getFullYear().toString().trim();
 
    const path = `/paySlips?employeeId=${user?.EmployeeId}&year=${currentYear}`;
    const URL = process.env.REACT_APP_BE_URL + path;
 
    const response = await axios.get(URL, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
 
    if (response.status === 200 && response.data.length > 0) {
      const payslipPaths = response?.data?.map(p => p.Path);
      let selectedPayslip = payslipPaths.find(path => {
        const parts = path.split('/');
        const year = parts[parts.length - 3]?.trim();
        const month = parts[parts.length - 2]?.toLowerCase().trim();
        return year === currentYear && month === currentMonth;
      });
 
      if (!selectedPayslip) {
        for (let i = 1; i <= 12; i++) {
          const prevDate = new Date();
          prevDate.setMonth(currentDate.getMonth() - i);
          const prevMonth = prevDate.toLocaleString("default", { month: "long" }).toLowerCase().trim();
          const prevYear = prevDate.getFullYear().toString().trim();
 
          selectedPayslip = payslipPaths.find(path => {
            const parts = path.split('/');
            const year = parts[parts.length - 3]?.trim();
            const month = parts[parts.length - 2]?.toLowerCase().trim();
            return year === prevYear && month === prevMonth;
          });
 
          if (selectedPayslip) break;
        }
      }
 
      if (selectedPayslip) {
        const parts = selectedPayslip.split('/');
        const payslipYear = parts[parts.length - 3]?.trim();
        const payslipMonth = parts[parts.length - 2]?.trim();
        const formattedMonth = payslipMonth.charAt(0).toUpperCase() + payslipMonth.slice(1);
 
        setSelectedPayslipMonth(formattedMonth);
        setSelectedPayslipYear(payslipYear);
        setSelectedPayslipPath(selectedPayslip);
      } else {
        setSelectedPayslipMonth('');
        setSelectedPayslipYear('');
        setSelectedPayslipPath('');
      }
    } else {
      setSelectedPayslipMonth('');
      setSelectedPayslipYear('');
      setSelectedPayslipPath('');
    }
  } catch (error) {
    console.error("Payslip fetch failed:", error);
    setSelectedPayslipMonth('');
    setSelectedPayslipYear('');
    setSelectedPayslipPath('');
  }
};
 
const handlePayslipDownload = () => {
  if (!selectedPayslipPath) {
    toast.error("No payslip available to download.", {
      position: toast.POSITION.TOP_CENTER,
    });
    return;
  }
 
  const link = document.createElement("a");
  link.href = selectedPayslipPath;
  link.download = `${selectedPayslipMonth}${selectedPayslipYear}.pdf`;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};



  //  7th one
  const [employeePercentage, setEmployeePercentage] = useState(null);
  const [personalInfo, setpersonalInfo] = useState(null);
  const [documents, setDocuments] = useState([]);

useEffect(() => {
  console.log("personalInfo", personalInfo?.personalInformation?.personalDetails?.experience?.totalExperience)
  if (personalInfo && personalInfo.personalInformation && personalInfo.personalInformation.personalDetails) {
    const docs = [];
    
    // Family documents
    if (personalInfo?.personalInformation?.personalDetails?.family?.birthCertificate) {
      const fileName = getFileName(personalInfo?.personalInformation?.personalDetails?.family?.birthCertificate);
      docs.push({
        name: 'Birth Certificate',
        url: personalInfo?.personalInformation?.personalDetails?.family?.birthCertificate,
        fileName: fileName,
        fileType: getFileType(fileName),
        uploadedOn: '01-01-2025',
        category: 'Family'
      });
    }
    if (personalInfo?.personalInformation?.personalDetails?.family?.marriageCertificate) {
      const fileName = getFileName(personalInfo?.personalInformation?.personalDetails?.family?.marriageCertificate);
      docs.push({
        name: 'Marriage Certificate',
        url: personalInfo?.personalInformation?.personalDetails?.family?.marriageCertificate,
        fileName: fileName,
        fileType: getFileType(fileName),
        uploadedOn: '01-01-2025',
        category: 'Family'
      });
    }
    
    // Health documents
    if (personalInfo?.personalInformation?.personalDetails?.health?.healthCertificate) {
      const fileName = getFileName(personalInfo?.personalInformation?.personalDetails?.health?.healthCertificate);
      docs.push({
        name: 'Health Certificate',
        url: personalInfo?.personalInformation?.personalDetails?.health?.healthCertificate,
        fileName: fileName,
        fileType: getFileType(fileName),
        uploadedOn: '01-01-2025',
        category: 'Health'
      });
    }
    
    // Additional Information documents
    if (personalInfo?.personalInformation?.personalDetails?.additionalInformation?.passportCopy) {
      const fileName = getFileName(personalInfo?.personalInformation?.personalDetails?.additionalInformation?.passportCopy);
      docs.push({
        name: 'Passport Copy',
        url: personalInfo?.personalInformation?.personalDetails?.additionalInformation?.passportCopy,
        fileName: fileName,
        fileType: getFileType(fileName),
        uploadedOn: '01-01-2025',
        category: 'Additional'
      });
    }
    if (personalInfo?.personalInformation?.personalDetails?.additionalInformation?.conductCertificates) {
      const fileName = getFileName(personalInfo?.personalInformation?.personalDetails?.additionalInformation?.conductCertificates);
      docs.push({
        name: 'Conduct Certificate',
        url: personalInfo?.personalInformation?.personalDetails?.additionalInformation?.conductCertificates,
        fileName: fileName,
        fileType: getFileType(fileName),
        uploadedOn: '01-01-2025',
        category: 'Additional'
      });
    }
    
    // Education documents
    if (personalInfo?.personalInformation?.personalDetails?.education?.educationCertificate) {
      const fileName = getFileName(personalInfo?.personalInformation?.personalDetails?.education?.educationCertificate);
      docs.push({
        name: 'Education Certificate',
        url: personalInfo?.personalInformation?.personalDetails?.education?.educationCertificate,
        fileName: fileName,
        fileType: getFileType(fileName),
        uploadedOn: '01-01-2025',
        category: 'Education'
      });
    }
    
    // Experience documents
    if (personalInfo?.personalInformation?.personalDetails?.experience?.experienceCertificate) {
      const fileName = getFileName(personalInfo?.personalInformation?.personalDetails?.experience?.experienceCertificate);
      docs.push({
        name: 'Experience Certificate',
        url: personalInfo?.personalInformation?.personalDetails?.experience?.experienceCertificate,
        fileName: fileName,
        fileType: getFileType(fileName),
        uploadedOn: '01-01-2025',
        category: 'Experience'
      });
    }
    
    // Submitted documents
    if (personalInfo?.personalInformation?.personalDetails?.submittedDocuments && Array.isArray(personalInfo?.personalInformation?.personalDetails?.submittedDocuments)) {
      personalInfo.personalInformation.personalDetails.submittedDocuments.forEach((doc, index) => {
        if (doc && doc.trim() !== '') {
          const fileName = getFileName(doc);
          docs.push({
            name: `Submitted Document ${index + 1}`,
            url: doc,
            fileName: fileName,
            fileType: getFileType(fileName),
            uploadedOn: '01-01-2025',
            category: 'Submitted'
          });
        }
      });
    }

    console.log('Processed documents:', docs); // Debug log
    setDocuments(docs);
  }
}, [personalInfo]);


  const getFileName = (url) => {
  if (!url) return '';
  
  try {
    // Remove query parameters first
    const urlWithoutParams = url.split('?')[0];
    // Extract filename from the clean URL
    const fileName = urlWithoutParams.substring(urlWithoutParams.lastIndexOf('/') + 1);
    // Decode URI component to handle encoded characters
    return decodeURIComponent(fileName);
  } catch (error) {
    console.error('Error extracting filename:', error);
    // Fallback: try to extract from original URL
    return url.substring(url.lastIndexOf('/') + 1).split('?')[0];
  }
};

// Function to handle document click/view
const handleDocumentView = (documentUrl, documentName) => {
  if (documentUrl) {
    try {
      // Open document in new tab
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening document:', error);
      toast.error(`Unable to open ${documentName}`, {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  } else {
    toast.error(`Document URL not available for ${documentName}`, {
      position: toast.POSITION.TOP_CENTER,
    });
  }
};

// Function to get file extension and icon
const getFileIcon = (fileName) => {
  if (!fileName) return 'ri-file-line';
  
  const extension = fileName.toLowerCase().split('.').pop();
  switch (extension) {
    case 'pdf':
      return 'ri-file-pdf-line';
    case 'doc':
    case 'docx':
      return 'ri-file-word-line';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'ri-image-line';
    case 'txt':
      return 'ri-file-text-line';
    case 'xls':
    case 'xlsx':
      return 'ri-file-excel-line';
    default:
      return 'ri-file-line';
  }
};

// Function to get file type for display
const getFileType = (fileName) => {
  if (!fileName) return 'Unknown';
  
  const extension = fileName.toLowerCase().split('.').pop();
  switch (extension) {
    case 'pdf':
      return 'PDF';
    case 'doc':
    case 'docx':
      return 'Word Document';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'Image';
    case 'txt':
      return 'Text File';
    case 'xls':
    case 'xlsx':
      return 'Excel';
    default:
      return extension ? extension.toUpperCase() : 'Unknown';
  }
};

  const getPersonalDetails = async () => {
    try {
      if (user) {
        const path = `/getPersonalDetails?EmployeeId=${user?.EmployeeId}`;
        const URL = process.env.REACT_APP_BE_URL + path;
        const response = await axios.get(URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        if (response.status === 200) {
          setEmployeePercentage(response.data)
          setpersonalInfo(response.data)
        } else {
          handleErrors(response);
        }
      }
    } catch (error) {
      if (error.response) {
        handleErrors(error.response);
      } else {
        toast.error("Something went wrong. Please try again later.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  }

  // holidays
  // Fetch Uploaded Files
  const [holidays, setHolidays] = useState([]);

  const fetchHolidays = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BE_URL}/getHolidays`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setHolidays(response.data || []);
      } else {
        handleErrors(response);
      }
    } catch (error) {
      setHolidays([]); // Show "No Data" if error occurs
    }
  };
  // announcements
  const [announcements, setAnnouncements] = useState([]);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BE_URL}/getAnnouncements`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        console.log("announcements", response.data || [])
        setAnnouncements(response.data || []);
      } else {
        handleErrors(response);
      }
    } catch (error) {
      setAnnouncements([]); // Show "No Data" if error occurs
    }
  };

  const calculateCompletionPercentage = () => {
    const personalInfoFields = [
      employeePercentage?.onboardEmployeeDetails?.Image ?? '',
      employeePercentage?.onboardEmployeeDetails?.FirstName ?? '',
      employeePercentage?.onboardEmployeeDetails?.LastName ?? '',
      employeePercentage?.onboardEmployeeDetails?.EmployeeId ?? '',
      employeePercentage?.onboardEmployeeDetails?.DateOfJoining ?? '',
      employeePercentage?.onboardEmployeeDetails?.OfficialEmail ?? '',
      employeePercentage?.onboardEmployeeDetails?.ContactNumber ?? '',
      employeePercentage?.onboardEmployeeDetails?.EmployeeType ?? '',
      employeePercentage?.onboardEmployeeDetails?.Department ?? '',
      employeePercentage?.onboardEmployeeDetails?.Designation ?? '',
      employeePercentage?.onboardEmployeeDetails?.UserAccessLevel ?? '',
      employeePercentage?.onboardEmployeeDetails?.DateOfBirth ?? '',
      employeePercentage?.onboardEmployeeDetails?.BloodGroup ?? '',
    ];

    const additionalInfoFields = [
      employeePercentage?.personalInformation?.personalDetails?.bank?.bankName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.bank?.branchName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.bank?.branchCode ?? '',
      employeePercentage?.personalInformation?.personalDetails?.bank?.accountNumber ?? '',
      employeePercentage?.personalInformation?.personalDetails?.bank?.accountType ?? '',
      employeePercentage?.personalInformation?.personalDetails?.bank?.swiftCode ?? '',
      employeePercentage?.personalInformation?.personalDetails?.family?.fatherName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.family?.motherName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.family?.birthCertificate ?? '',
      employeePercentage?.personalInformation?.personalDetails?.family?.spouseName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.family?.marriageCertificate ?? '',
      employeePercentage?.personalInformation?.personalDetails?.family?.children ?? '',
      // Assuming there may be multiple children
      ...(employeePercentage?.personalInformation?.personalDetails?.family?.childrenDetails?.map(child => child?.ChildrenName ?? '') ?? []),
      employeePercentage?.personalInformation?.personalDetails?.education?.institutionName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.education?.institutionCode ?? '',
      employeePercentage?.personalInformation?.personalDetails?.education?.institutionType ?? '',
      employeePercentage?.personalInformation?.personalDetails?.education?.qualification ?? '',
      employeePercentage?.personalInformation?.personalDetails?.education?.graduationDate ?? '',
      employeePercentage?.personalInformation?.personalDetails?.education?.grade ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.medicalConditions ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.knownChronicConditions ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.knownChronicAllergies ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.vaccinationStatus ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.vaccinationType ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.dateOfLastVaccination ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.workRelatedInjuries ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.dateOfIncident ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.incidentDescription ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.doYouHavePersonalDoctor ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.doctorName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.doctorContact ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.doctorEmail ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.hospitalName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.health?.healthCertificate ?? '',
      employeePercentage?.personalInformation?.personalDetails?.additionalInformation?.kin ?? '',
      employeePercentage?.personalInformation?.personalDetails?.additionalInformation?.passport ?? '',
      employeePercentage?.personalInformation?.personalDetails?.additionalInformation?.passportCopy ?? '',
      employeePercentage?.personalInformation?.personalDetails?.additionalInformation?.conductCertificates ?? '',
      employeePercentage?.personalInformation?.personalDetails?.additionalInformation?.referenceDetails ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.totalExperience ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.employerName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.department ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.designation ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.managerName ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.managerEmail ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.startDate ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.endDate ?? '',
      employeePercentage?.personalInformation?.personalDetails?.experience?.location ?? '',
      // Assuming there may be multiple submitted documents
      ...(employeePercentage?.personalInformation?.personalDetails?.submittedDocuments ?? []),
    ];

    const allFields = [...personalInfoFields, ...additionalInfoFields];
    const filledFields = allFields.filter(field => field && field.trim() !== '').length;

    return Math.round((filledFields / allFields.length) * 100);
  };

  const completionPercentage = calculateCompletionPercentage();

  return (
    <div>
      <ToastContainer />
      <div class="content-page">
        <div class="container-fluid">
          <div class="row">
            <div class="col-lg-12">
              <div class="row mb-3">
                <div class="col">
                  <h3 class="title mb-2">Welcome to <span>Work</span><span>Wise</span> 360!</h3>
                </div>
                {user?.UserAccessLevel.toLowerCase() === "admin" ?
                  <div className="col-auto pr-0">
                    <div className="switch-btn">
                      <input
                        className="switch"
                        type="checkbox"
                        id="switch-view"
                        name="switch-view-checkbox"
                        value="switch-view-checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            window.location.href = '/Dashboard';  // Redirect to admin dashboard
                          } else {
                            window.location.href = '/home';  // Redirect to user dashboard
                          }
                        }}
                      />
                      <label htmlFor="switch-view" className="switch-label">
                        <i className="ri-user-line"></i>
                        <div className="action">
                          <span className="option-1">Switch Admin View</span>
                          <span className="option-2">Switch User View</span>
                        </div>
                      </label>
                    </div>
                  </div> : ""}
                <div class="col-auto">
                  <a href="/SubmitTimesheet" data-extra-toggle="tab" data-target="#timesheet-pan" class="btn btn-primary mt-lg-0 mt-4"><i class="ri-calendar-schedule-fill"></i> Timesheet</a>
                </div>
              </div>
            </div>
            <div class="col-lg-12">
              <div class="row">
                <div className="col-lg-3 col-md-6">
                  <div className="card dashboard-card mb-4 blue">
                    <div className="card-body pt-2">
                      <a href="#" data-toggle="modal" data-target="#assignedProjectsModal" className="stretched-link"></a>
                      <div className="d-flex align-items-top justify-content-between">
                        <div className="icon icon-box rounded">
                          <i className="ri-contract-fill"></i>
                        </div>
                        <h6>{statusCounts['Active'] + statusCounts['Assigned']}<span>Active</span></h6>
                      </div>
                      <h5 className="title mb-0">Assigned Project(s)</h5>
                      <small className="mb-0 text-muted">View Details</small>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="card dashboard-card mb-4 amber">
                    <div className="card-body pt-2">
                      <Link to={'/ViewTimesheet'} target="_blank" className="stretched-link"></Link>
                      <div className="d-flex align-items-top justify-content-between">
                        <div className="icon icon-box rounded">
                          <i className="ri-calendar-schedule-fill"></i>
                        </div>
                        <h6><small className="status-tag">Submitted</small> {submittedCount} <span>Week(s)</span></h6>
                      </div>
                      <h5 className="title mb-0">Timesheet Status</h5>
                      <small className="mb-0 text-muted">View Details</small>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="card dashboard-card mb-4 green">
                    <div className="card-body pt-2">
                      <a href="#" data-toggle="modal" data-target="#leaveBalanceModal" className="stretched-link"></a>
                      <div className="d-flex align-items-top justify-content-between">
                        <div className="icon icon-box rounded">
                          <i className="ri-timer-fill"></i>
                        </div>
                        <h6>{totalLeaves || 0}<span>As on {new Date().toLocaleString('default', { month: 'long' })} {new Date().getFullYear()}</span></h6>
                      </div>
                      <h5 className="title mb-0">Leave Balance</h5>
                      <small className="mb-0 text-muted">View Details</small>
                    </div>
                  </div>
                </div>
                {/* <div className="col-lg-3 col-md-6">
                  <div className="card dashboard-card mb-4 red">
                    <div className="card-body pt-2">
                      <a href="#" data-toggle="modal" data-target="#insurancePoliciesModal" className="stretched-link"></a>
                      <div className="d-flex align-items-top justify-content-between">
                        <div className="icon icon-box rounded">
                          <i className="ri-shield-check-fill"></i>
                        </div>
                        <h6>{policies?.length}<span>Year 2025</span></h6>
                      </div>
                      <h5 className="title mb-0">Insurance Policies</h5>
                      <small className="mb-0 text-muted">View Details</small>
                    </div>
                  </div>
                </div> */}
                <div className="col-lg-3 col-md-6">
                  <div className="card dashboard-card mb-4 red">
                    <div className="card-body pt-2">
                      <Link to={'/MedicalInsurance'} target="_blank" className="stretched-link"></Link>
                      <div className="d-flex align-items-top justify-content-between">
                        <div className="icon icon-box rounded">
                          <i className="ri-calendar-schedule-fill"></i>
                        </div>
                        <h6>{policies?.length || 0}<span>Year 2025</span></h6>
                      </div>
                      <h5 className="title mb-0">Insurance Policies</h5>
                      <small className="mb-0 text-muted">View Details</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="card border-none mb-4 dashboard-card">
                <div class="card-body">
                  <ChartComponent />
                </div>
              </div>
              <div class="card border-none mb-4">
                <div class="card-body">
                  <div className="row align-items-center text-center py-2">
                    <div className="profile-info col-xl-3 col-lg-3 col-md-3 col-sm-3">
                      <div className="profile-icon rounded m-auto bg-info">
                        <i className="ri-hand"></i>
                      </div>
                      <h5 className="mb-2 mt-3 icon-text-danger">
                        {personalInfo?.personalInformation?.personalDetails?.experience?.totalExperience || 0}
                      </h5>
                      <p className="mb-0">Experience</p>
                    </div>
                    <div className="profile-info col-xl-3 col-lg-3 col-md-3 col-sm-3">
                      <div className="profile-icon rounded m-auto bg-warning">
                        <i className="ri-medal-line"></i>
                      </div>
                      <h5 className="mb-2 mt-3 icon-text-warning">{personalInfo?.personalInformation?.personalDetails?.achievements?.length || 0}</h5>
                      <p className="mb-0">Achievements </p>
                    </div>
                    <div className="profile-info col-xl-3 col-lg-3 col-md-3 col-sm-3" style={{ color: 'gray' }}>
                      <div className="profile-icon rounded m-auto bg-success" style={{ backgroundColor: 'lightgray' }}>
                        <i className="ri-code-box-line"></i>
                      </div>
                      <h5 className="mb-2 mt-3 icon-text-info">0</h5>
                      <p className="mb-0">Certificate</p>
                    </div>
                    <div className="profile-info col-xl-3 col-lg-3 col-md-3 col-sm-3" style={{ color: 'gray' }}>
                      <div className="profile-icon rounded m-auto bg-danger" style={{ backgroundColor: 'lightgray' }}>
                        <i className="ri-verified-badge-line"></i>
                      </div>
                      <h5 className="mb-2 mt-3 icon-text-success">0</h5>
                      <p className="mb-0">Participated</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="row">
                <div class="col-lg-4 col-md-4 col-sm-6">
                  <div class="card border-none mb-4">
                    <div className="card-body text-center p-2">
                      <a href="#" data-toggle="modal" data-target="#recentRequestStatusModal" className="stretched-link"></a>
                      <div className="row justify-content-center">
                        <div className="col-lg-8 col-md-8">
                          <div className="card ww-app-bg status-icon border-none mt-2">
                            <div className="card-body">
                              <span><i className="ri-list-check-3"></i></span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <h6 className="mt-2 mb-0">Status</h6>
                      <small className="text-muted d-block mb-2">Recent Request(s)</small>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-md-4 col-sm-6">
                  <div className="card border-none mb-4">
                    <div
                      className="card-body text-center p-2"
                      onClick={handlePayslipDownload}
                      style={{ cursor: selectedPayslipPath ? 'pointer' : 'not-allowed' }}
                    >
                      <div className="row justify-content-center">
                        <div className="col-lg-8 col-md-8">
                          <div className="card ww-app-bg recent-payslip-icon border-none mt-2">
                            <div className="card-body">
                              <span><i className="ri-file-list-3-line"></i></span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <h6 className="mt-2 mb-0">
                        {selectedPayslipMonth && selectedPayslipYear ? (
                          `${selectedPayslipMonth} ${selectedPayslipYear}`
                        ) : (
                          "No Payslip Found"
                        )}
                      </h6>
                      <small className="text-muted d-block mb-2">Recent Payslip</small>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-md-4 col-sm-12">
                  <div className="card border-none mb-4">
                    <div className="card-body text-center p-0">
                      <a href="/profile" className="stretched-link"></a>
                      <div className="user-profile-donut">
                        <svg width="100%" height="100%" viewBox="0 0 45 45" className="donut">
                          <circle className="donut-segment" cx="23" cy="23" r="15.91549430918954" fill="transparent" stroke="#f3f3f3" strokeWidth="1" strokeDashoffset="25"></circle>
                          <circle className="donut-segment" cx="23" cy="23" r="15.91549430918954" fill="transparent" stroke="#004F7C" strokeWidth="2" strokeDasharray={`${completionPercentage} ${100 - completionPercentage}`} strokeDashoffset="25"></circle>
                          <g className="chart-text">
                            <text x="50%" y="50%" className="chart-number">
                              {completionPercentage}%
                            </text>
                            <text x="50%" y="50%" className="chart-label">
                              Profile Strength
                            </text>
                          </g>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <ul class="nav nav-pills tab-pills" id="pills-tab" role="tablist">
                <li class="nav-item">
                  <a class="nav-link active" data-toggle="pill" href="#announcements" role="tab">Announcements <span class="badge badge-tab">{announcements?.length || 0}</span></a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" data-toggle="pill" href="#holidays" role="tab">Holidays <span class="badge badge-tab">{holidays?.length || 0}</span></a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" data-toggle="pill" href="#my-documents" role="tab">My Documents <span class="badge badge-tab">{documents?.length || 0}</span></a>
                </li>
              </ul>
              <div className="tab-content task-content">
                <div className="tab-pane fade show active" id="announcements" role="tabpanel">
                  <div className="card border-none">
                    <div className="card-body">
                      <div className="announcement-list dashboard-tab-scroll">
                        <div className="announcement-container">
                          <ul className="announcement">
                            {announcements && announcements.length > 0 ? (
                              announcements.map((announcement, index) => (
                                <li className="announcement-item" key={index}>
                                  <div className="timestamp">
                                    {`${moment(announcement.eventStartDate).format("DD MMMM YYYY")} - Posted By - ${announcement.employeeName}`}
                                  </div>
                                  <div className="item-title">{announcement.subject}</div>
                                  <p>{announcement.details}</p>
                                </li>
                              ))
                            ) : (
                              <li className="announcement-item">
                                <p>No data.....</p>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="tab-pane fade" id="holidays" role="tabpanel">
                  <div class="card border-none">
                    <div class="card-body">
                      <div class="card-body p-0">
                        <div class="holiday-list dashboard-tab-scroll">
                          <table class="table grid">
                            <thead class="bg-light">
                              <tr>
                                <th>S.No.</th>
                                <th>Holidays</th>
                                <th>Day</th>
                                <th>Date</th>
                                <th>Type</th>
                              </tr>
                            </thead>
                            <tbody>
                              {holidays?.map((holiday, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{holiday.holidayName}</td>
                                  <td>{holiday.day}</td>
                                  <td>{moment(holiday.date, "YYYY-MM-DD HH:mm:ss").format("DD-MM-YYYY")}</td>
                                  <td>{holiday.type}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="tab-pane fade" id="my-documents" role="tabpanel">
                  <div class="card border-none">
                    <div class="card-body">
                      <div class="document-list dashboard-tab-scroll">
                        {documents && documents.length > 0 ? (
                          <div class="table-responsive">
                            <table class="table grid">
                              <thead class="bg-light">
                                <tr>
                                  <th>S.No.</th>
                                  <th>Document Name</th>
                                  <th>File Details</th>
                                  <th>Category</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {documents.map((doc, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <div class="d-flex align-items-center">
                                        <i class={`${getFileIcon(doc.fileName)} font-size-18 text-muted mr-2`}></i>
                                        <span>{doc.name}</span>
                                      </div>
                                    </td>
                                    <td>
                                      <div>
                                        <div class="font-weight-medium">{doc.fileName}</div>
                                        <small class="text-muted">{doc.fileType}</small>
                                      </div>
                                    </td>
                                    <td>
                                      <span class={`badge badge-${doc.category.toLowerCase() === 'family' ? 'info' : 
                                        doc.category.toLowerCase() === 'health' ? 'success' : 
                                        doc.category.toLowerCase() === 'education' ? 'warning' : 
                                        doc.category.toLowerCase() === 'experience' ? 'primary' : 
                                        doc.category.toLowerCase() === 'submitted' ? 'secondary' : 'light'}`}>
                                        {doc.category}
                                      </span>
                                    </td>
                                    <td>
                                      <button 
                                        class="btn btn-sm btn-outline-primary"
                                        onClick={() => handleDocumentView(doc.url, doc.name)}
                                        title={`View ${doc.name}`}
                                      >
                                        <i class="ri-eye-line"></i> View
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div class="text-center py-4">
                            <i class="ri-file-list-line font-size-48 text-muted"></i>
                            <p class="text-muted mt-2">No documents available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <!-- Assigned projects modal --> */}
      <div className="modal fade" id="assignedProjectsModal" tabIndex="-1" role="dialog" aria-labelledby="assignedProjectsModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header py-2">
              <h6 className="modal-title" id="assignedProjectsModalLabel">Assigned Projects</h6>
              <button className="close" type="button" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true"><i className="ri-close-circle-fill font-size-20"></i></span>
              </button>
            </div>
            <div className="modal-body">
              <div className="table-responsive">
                <table className="table grid">
                  <thead className="bg-light">
                    <tr>
                      <th>S.No.</th>
                      <th>Projects</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedProjects.map((project, index) => (
                      <tr key={project.id}>
                        <td className="status bg-active">{index + 1}</td>
                        <td>{project.projectName}</td>
                        <td>
                          <span className={`badge border border-info text-info status`}>
                            <i className="ri-thumb-up-fill"></i> {project.projectStatus}
                          </span>
                        </td>
                        <td>{moment(project.startDate).format("DD-MM-YYYY")}</td>
                        <td>{moment(project.endDate).format("DD-MM-YYYY")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Leave Balance modal */}
      <div className="modal fade" id="leaveBalanceModal" tabIndex="-1" role="dialog" aria-labelledby="leaveBalanceModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header py-2">
              <h6 className="modal-title" id="leaveBalanceModalLabel">Leave Balance</h6>
              <button className="close" type="button" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true"><i className="ri-close-circle-fill font-size-20"></i></span>
              </button>
            </div>
            <div className="card mb-4">
              <div className="card-body">
                <div className="row text-center border-bottom">
                  <div className="col-lg-12 col-md-12 border-bottom">
                    <div>
                      <label>Total Leaves</label>
                      <h4 className="text-success">{totalLeaves}</h4>
                    </div>
                  </div>
                  {leaveData?.leaveBalance?.slice(0, 3).map((leave, index) => (
                    <div className={`col-lg-4 col-md-4 col-sm-4 ${index !== 0 ? 'border-right border-left' : ''}`} key={index}>
                      <div className={`claim-history-box ${leave.leaveType.toLowerCase().replace(' ', '-')}-count`}>
                        <label> {toCamelCase(leave.leaveType)}</label>
                        <h4 className="text-info">{leave.noOfLeaves}</h4>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="row text-center border-bottom collapse" id="moreLeaves">
                  {leaveData?.leaveBalance?.slice(3).map((leave, index) => (
                    <div className={`col-lg-4 col-md-4 col-sm-4 ${index !== 0 ? 'border-right border-left' : ''}`} key={index}>
                      <div className={`claim-history-box ${leave.leaveType.toLowerCase().replace(' ', '-')}-count`}>
                        <label> {toCamelCase(leave.leaveType)}</label>
                        <h4 className="text-info">{leave.noOfLeaves}</h4>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-2">
                  <a href="#" data-toggle="collapse" data-target="#moreLeaves">More Leaves</a>
                </div>
                <div className="row text-center">
                  <div className="col-lg-12 col-md-12 border-bottom">
                    <p className="mt-3">Quick summary of all submitted requests.</p>
                    <div>
                      <label>Total Requests</label>
                      <h4>{approvedRequests + submittedRequests}</h4>
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-4">
                    <div className="claim-history-box pending-count">
                      <label>Pending</label>
                      <h4 className="text-warning">{submittedRequests}</h4>
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-4 border-right border-left">
                    <div className="claim-history-box approved-count">
                      <label>Approved</label>
                      <h4 className="text-success">{approvedRequests}</h4>
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-4">
                    <div className="claim-history-box rejected-count">
                      <label>Rejected</label>
                      <h4 className="text-light">{RejectedRequests}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* request status */}
      <div className="modal fade" id="recentRequestStatusModal" tabIndex="-1" role="dialog" aria-labelledby="recentRequestStatusModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-md modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header py-2">
              <h6 className="modal-title" id="recentRequestStatusModalLabel">Recent Request Status</h6>
              <button className="close" type="button" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true"><i className="ri-close-circle-fill font-size-20"></i></span>
              </button>
            </div>
            <div className="modal-body">
              <div className="table-responsive">
                <table className="table grid">
                  <thead className="bg-light">
                    <tr>
                      <th>S.No.</th>
                      <th>Requests</th>
                      <th>Status</th>
                      <th>Request Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests?.map((request, index) => (
                      <tr key={request.requestId}>
                        <td className={`status ${request.status === 'Pending' ? 'bg-pending' : request.status === 'Rejected' ? 'bg-rejected' : request.status === 'Approved' ? 'bg-approved' : ''}`}>
                          {index + 1}
                        </td>
                        <td>{request.requestId}</td>
                        <td>
                          {request.status.trim().toLowerCase() === 'approved' ? (
                            <span className="badge border border-success text-success status">
                              <i className="ri-thumb-up-fill"></i> {request.status.trim()}
                            </span>
                          ) : request.status.trim().toLowerCase() === 'rejected' ? (
                            <span className="badge border border-danger text-danger status">
                              <i className="ri-thumb-down-line"></i> {request.status.trim()}
                            </span>
                          ) : request.status.trim().toLowerCase() === 'inprogress' ? (
                            <span className="badge border border-warning text-warning status">
                              <i className="ri-hourglass-fill"></i> {request.status.trim()}
                            </span>
                          ) : (
                            <span className="badge border border-secondary text-secondary status">
                              <i className="ri-thumb-up-fill"></i> {request.status.trim()}
                            </span>
                          )}
                        </td>
                        <td>{moment(request.requestedDate).format('DD-MM-YYYY')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* policies model */}
      {/* <div className="modal fade" id="insurancePoliciesModal" tabindex="-1" role="dialog" aria-labelledby="insurancePoliciesModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header py-2">
              <h6 className="modal-title" id="insurancePoliciesModalLabel">Policies</h6>
              <button className="close" type="button" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true"><i className="ri-close-circle-fill font-size-20"></i></span>
              </button>
            </div>
            <div className="modal-body">
              <div className="table-responsive">
                <table className="table grid">
                  <thead className="bg-light">
                    <tr>
                      <th>S.No.</th>
                      <th>Type</th>
                      <th>Policies</th>
                      <th>Policy File</th>
                      <th>Created Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policies.map((policy, index) => (
                      <tr key={policy.policyId}>
                        <td className={`status ${policy.status === 'Active' ? 'bg-active' : policy.status === 'Expired' ? 'bg-expired' : policy.status === 'Pending' ? 'bg-pending' : 'bg-rejected'}`}>
                          {index + 1}
                        </td>
                        <td>{policy.policyType}</td>
                        <td>{policy.policyName}</td>
                        <td><a href={policy.policyFileURL} target="_blank" rel="noopener noreferrer">{getFileName(policy.policyFileURL)}</a></td>
                        <td>{moment(policy.createdDate).format('DD-MM-YYYY')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard;


